import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  TextField,
  Button,
  Alert,
  Typography,
  Paper,
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  Divider,
  Link,
  Chip,
  GlobalStyles,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { motion } from "framer-motion";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";

const COLORS = {
  orange: "#d37d36",
  orangeDark: "#b8662b",
  blue: "#1e4b81",
  blueDark: "#102840",
  white: "#ffffff",
  bg: "#f7f9fc",
  text: "#1d2f43",
  secondary: "#64748b",
  border: "rgba(16, 40, 64, 0.10)",
  borderSoft: "rgba(16, 40, 64, 0.07)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: COLORS.white,
    minHeight: 46,
    boxShadow: "0 8px 22px rgba(16,40,64,0.035)",
    "& fieldset": {
      borderColor: "rgba(16,40,64,0.13)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(211,125,54,0.46)",
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.orange,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-input": {
    py: 1.25,
    fontSize: "0.9rem",
    fontWeight: 650,
    color: COLORS.blueDark,
  },
};

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje("");
    setError("");

    const emailTrimmed = email.trim();

    if (!emailTrimmed) {
      setError("Introduce tu correo electrónico.");
      return;
    }

    if (!validateEmail(emailTrimmed)) {
      setError("Introduce un correo electrónico válido.");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post(API_ENDPOINTS.auth.forgotPassword, { email: emailTrimmed });
      setMensaje("Si el correo está registrado, recibirás un enlace para restablecer tu contraseña.");
    } catch (err) {
      setError(err.response?.data?.message || "Error al enviar el correo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          html: {
            fontSize: "14.5px",
            overflowX: "hidden",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            backgroundColor: COLORS.bg,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "*": {
            boxSizing: "border-box",
          },
          "@keyframes pulseDot": {
            "0%": { transform: "scale(1)", opacity: 1 },
            "60%": { transform: "scale(1.9)", opacity: 0 },
            "100%": { transform: "scale(1.9)", opacity: 0 },
          },
        }}
      />

      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
          bgcolor: COLORS.bg,
          background:
            "radial-gradient(circle at 14% 14%, rgba(30,75,129,0.11), transparent 30%), radial-gradient(circle at 86% 16%, rgba(211,125,54,0.13), transparent 28%), linear-gradient(180deg,#ffffff 0%,#f7f9fc 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.58,
            backgroundImage:
              "linear-gradient(rgba(16,40,64,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(16,40,64,0.035) 1px, transparent 1px)",
            backgroundSize: "52px 52px",
            pointerEvents: "none",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 430, position: "relative", zIndex: 1 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.4, sm: 3.2 },
              borderRadius: "28px",
              border: `1px solid ${COLORS.borderSoft}`,
              bgcolor: "rgba(255,255,255,0.94)",
              boxShadow:
                "0 24px 70px rgba(16,40,64,0.10), inset 0 1px 0 rgba(255,255,255,0.92)",
              backdropFilter: "blur(18px)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2.3 }}>
              <Stack direction="row" spacing={1.15} alignItems="center" justifyContent="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "16px",
                    bgcolor: COLORS.white,
                    border: `1px solid ${COLORS.borderSoft}`,
                    boxShadow: "0 12px 30px rgba(16,40,64,0.08)",
                    display: "grid",
                    placeItems: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src="/images/logo_simbolo.png"
                    alt="DQPa"
                    sx={{ width: 36, height: 36, objectFit: "contain" }}
                  />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: COLORS.blueDark,
                      fontSize: "1.45rem",
                      fontWeight: 950,
                      letterSpacing: "-0.05em",
                      lineHeight: 1,
                    }}
                  >
                    DQPa
                  </Typography>
                  <Typography
                    sx={{
                      color: COLORS.secondary,
                      fontSize: "0.64rem",
                      fontWeight: 800,
                      letterSpacing: "0.055em",
                      textTransform: "uppercase",
                      mt: 0.4,
                    }}
                  >
                    Data Quality Process Assessment
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ textAlign: "center", mb: 2.4 }}>
              <Chip
                icon={<LockResetOutlinedIcon sx={{ color: `${COLORS.orange} !important`, fontSize: "17px !important" }} />}
                label="Recuperación de acceso"
                size="small"
                sx={{
                  height: 28,
                  mb: 1.1,
                  bgcolor: "rgba(211,125,54,0.09)",
                  color: COLORS.blueDark,
                  fontWeight: 900,
                  border: "1px solid rgba(211,125,54,0.16)",
                  "& .MuiChip-label": { fontSize: "0.72rem" },
                }}
              />

              <Typography
                component="h1"
                sx={{
                  color: COLORS.blueDark,
                  fontWeight: 950,
                  fontSize: { xs: "1.45rem", sm: "1.65rem" },
                  letterSpacing: "-0.04em",
                  lineHeight: 1.1,
                  mb: 0.75,
                }}
              >
                Recupera tu contraseña
              </Typography>

              <Typography sx={{ color: COLORS.secondary, fontSize: "0.88rem", lineHeight: 1.55 }}>
                Introduce tu correo y te enviaremos un enlace para restablecer el acceso a tu cuenta.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.35,
                borderRadius: "16px",
                bgcolor: "rgba(30,75,129,0.045)",
                border: "1px solid rgba(30,75,129,0.07)",
                mb: 1.65,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ShieldOutlinedIcon sx={{ color: COLORS.blue, fontSize: 19, mt: 0.15 }} />
                <Box>
                  <Typography sx={{ color: COLORS.blueDark, fontWeight: 900, fontSize: "0.78rem", mb: 0.2 }}>
                    Proceso seguro
                  </Typography>
                  <Typography sx={{ color: COLORS.secondary, fontSize: "0.76rem", lineHeight: 1.45 }}>
                    Por seguridad, solo se enviará el enlace si el correo está asociado a una cuenta existente.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Stack spacing={1.65}>
                <Box>
                  <Typography sx={{ fontWeight: 850, mb: 0.65, color: COLORS.blueDark, fontSize: "0.82rem" }}>
                    Correo electrónico
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@empresa.com"
                    required
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: COLORS.secondary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                </Box>

                {mensaje && (
                  <Alert
                    icon={<MarkEmailReadOutlinedIcon fontSize="inherit" />}
                    severity="success"
                    sx={{ borderRadius: "14px", fontWeight: 650, fontSize: "0.84rem" }}
                  >
                    {mensaje}
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ borderRadius: "14px", fontWeight: 650, fontSize: "0.84rem" }}>
                    {error}
                  </Alert>
                )}

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  endIcon={!loading ? <ArrowForwardIcon /> : null}
                  sx={{
                    mt: 0.2,
                    py: 1.25,
                    minHeight: 46,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 900,
                    fontSize: "0.92rem",
                    bgcolor: COLORS.blue,
                    boxShadow: "0 14px 30px rgba(30,75,129,0.20)",
                    transition: "all .22s ease",
                    "&:hover": {
                      bgcolor: COLORS.blueDark,
                      boxShadow: "0 18px 38px rgba(16,40,64,0.26)",
                      transform: "translateY(-1px)",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(30,75,129,0.45)",
                      color: COLORS.white,
                    },
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Enviar enlace"}
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 2.45, borderColor: COLORS.borderSoft }} />

            <Typography align="center" sx={{ color: COLORS.secondary, fontSize: "0.84rem", lineHeight: 1.45 }}>
              ¿Ya tienes acceso?{" "}
              <Link
                component={RouterLink}
                to="/login"
                sx={{
                  textDecoration: "none",
                  color: COLORS.orange,
                  fontWeight: 900,
                  "&:hover": { color: COLORS.orangeDark },
                }}
              >
                Iniciar sesión
              </Link>
            </Typography>
          </Paper>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 2.2 }}>
            <Box
              sx={{
                position: "relative",
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: COLORS.orange,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: COLORS.orange,
                  animation: "pulseDot 2s ease-out infinite",
                },
              }}
            />
            <Typography align="center" sx={{ color: COLORS.secondary, fontSize: "0.74rem", lineHeight: 1.55 }}>
              Restablecimiento protegido para recuperar el acceso a DQPa.
            </Typography>
          </Stack>
        </motion.div>
      </Box>
    </>
  );
}

export default ForgotPassword;
